import { useMemo, useState } from 'react';

import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { useDeleteFiles } from '@/api/mutations/useDeleteFiles';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import type { File } from '@/api/types/backend-dto-types';
import { formatBytes } from '@/new/components/apideck/ApideckFilePickerCustom/utils/bytesToSize';
import { FilterMultiSelect } from '@/new/components/filter/filter-multi-select';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { formatDatetimeForTable, includedInArr, selectColumn } from '@/new/lib/table-utils';

const fallbackTableData: File[] = [];
const columnHelper = createColumnHelper<File>();

export function FilesTable({ files, isLoading }: { files: File[]; isLoading: boolean }) {
  const { matterId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingFileIds, setDeletingFileIds] = useState<Set<string>>(new Set());
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  if (!matterId) return null;

  // Get unique file extensions from the files
  const fileExtensions = useMemo(() => {
    const extensions = files.map((file) => file.extension).filter((ext): ext is string => !!ext);
    return Array.from(new Set(extensions)).sort();
  }, [files]);

  const clearAllFilters = () => {
    setSearchQuery('');
    setColumnFilters([]);
  };

  // Fetch matter to get all docs caseId - to be removed when files module is re-worked to use matterId
  const { data: matter } = useGetMatterV2(matterId);
  const { mutate: deleteFiles, isLoading: isDeletingFiles } = useDeleteFiles({
    caseId: matter?.allDocumentsCaseId ?? '',
    matterId,
  });

  const columns = useMemo(
    () => [
      selectColumn<File>(),
      columnHelper.accessor('name', {
        header: () => <span>Filename</span>,
        cell: (info) => (
          <Tooltip delayDuration={500}>
            <TooltipTrigger className="flex items-center">
              <span className="line-clamp-1 text-left break-all">{info.getValue()}</span>
            </TooltipTrigger>
            <TooltipContent>{info.getValue()}</TooltipContent>
          </Tooltip>
        ),
        meta: {
          sortIcon: 'string',
        },
      }),
      columnHelper.accessor('size', {
        header: () => <span>File Size</span>,
        cell: (info) => formatBytes(info.getValue()),
        meta: {
          sortIcon: 'number',
        },
      }),
      columnHelper.accessor('extension', {
        header: () => <span>Extension</span>,
        cell: (info) => (
          <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600 uppercase">
            {info.getValue()}
          </span>
        ),
        filterFn: includedInArr,
        meta: {
          sortIcon: 'string',
        },
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Uploaded</span>,
        cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
        meta: {
          sortIcon: 'number',
        },
      }),
      columnHelper.display({
        id: 'delete',
        header: () => <span></span>,
        cell: (info) => (
          <div className="flex items-center">
            <Button
              size="iconSm"
              variant="ghost"
              className="p-0 hover:text-red-500"
              onClick={() => {
                const fileId = info.row.original.id;
                setDeletingFileIds((prev) => new Set(prev).add(fileId));
                deleteFiles([fileId], {
                  onSettled: () => {
                    setDeletingFileIds((prev) => {
                      const next = new Set(prev);
                      next.delete(fileId);
                      return next;
                    });
                  },
                });
              }}
            >
              <Icon className="lucide--x" />
            </Button>
          </div>
        ),
      }),
    ],
    [deleteFiles, setDeletingFileIds],
  );

  const table = useReactTable({
    data: files ?? fallbackTableData,
    columns,
    state: {
      rowSelection,
      globalFilter: searchQuery,
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setSearchQuery,
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 50,
      },
      sorting: [{ id: 'createdDate', desc: true }],
    },
    meta: {
      getRowClassName: (row: File) => {
        return deletingFileIds.has(row.id) ? 'opacity-50' : '';
      },
    },
  });

  // Get selected file IDs
  const selectedFileIds = Object.keys(rowSelection);

  const handleRemoveSelectedFiles = () => {
    if (selectedFileIds.length === 0) return;

    selectedFileIds.forEach((fileId) => {
      setDeletingFileIds((prev) => new Set(prev).add(fileId));
    });

    deleteFiles(selectedFileIds, {
      onSettled: () => {
        setDeletingFileIds((prev) => {
          const next = new Set(prev);
          selectedFileIds.forEach((fileId) => next.delete(fileId));
          return next;
        });
        setRowSelection({});
      },
    });
  };

  const extensionFilterValue = table.getColumn('extension')?.getFilterValue();
  const selectedExtensions = Array.isArray(extensionFilterValue) ? extensionFilterValue : [];

  return (
    <ScrollableTable
      className="-mx-1 grow px-1"
      table={table}
      plural="uploaded files"
      globalFilter={searchQuery}
      setGlobalFilter={setSearchQuery}
      loading={isLoading}
      filterPopover={
        <FilterPopoverContent clearAllFilters={clearAllFilters}>
          <div>
            <p className="mb-3 text-gray-600">File Extension</p>
            <FilterMultiSelect
              values={selectedExtensions}
              options={fileExtensions}
              column={table.getColumn('extension')!}
              placeholder="Select file extensions..."
              formatLabel={(ext) => ext.toUpperCase()}
            />
          </div>
        </FilterPopoverContent>
      }
      filterCount={columnFilters.length}
      clearAllFilters={clearAllFilters}
      additionalButtons={
        <Button disabled={selectedFileIds.length === 0 || isDeletingFiles} onClick={handleRemoveSelectedFiles}>
          <Icon className="lucide--trash" />
          <span>Remove Selected ({selectedFileIds.length})</span>
        </Button>
      }
    />
  );
}
